import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import TagPicker from './tag_picker';
import { addTagToRecord, getTagsForRecord, removeTagFromRecord } from '../../helpers/prospect_api';
import { isEmpty, uniq } from 'lodash';
import { displayToast } from '../../helpers/display_toast';
import TagModal from './tag_modal';
import type { AddTagButtonProps } from './add_tag_button';

type Props = {
  buttonProperties: AddTagButtonProps;
  initialTags?: string[];
  insightsPropertyId?: string;
  recordId?: number;
  recordType?: string;
  setShowTagsHeader?: (show: boolean) => void;
}

export default function ConnectTagManager({
  buttonProperties = {},
  recordType,
  recordId,
  insightsPropertyId,
  initialTags,
  setShowTagsHeader
}: Props) {
  const [tags, setTags] = useState(initialTags);

  // Use two states here to allow the tagForModal to linger. This makes closing the tag modal less jarring
  const [tagForModal, setTagForModal] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (tags === undefined) getTagsForRecord(recordType, recordId, insightsPropertyId).then(setTags);
  }, []);

  const handleTagSelect = (newTag: string) => {
    // The network request shouldn't ever really fail so opting to just update state immediately
    setTags(prevTags => uniq([...(prevTags || []), newTag]));

    addTagToRecord(newTag, recordType, recordId, insightsPropertyId)
      .then(() => displayToast('Tag has been added', 'success'));
  };

  const handleTagDelete = (tag: string) => {
    // The network request shouldn't ever really fail so opting to just update state immediately
    setTags(prevTags => (prevTags || []).filter(existingTag => existingTag !== tag));

    removeTagFromRecord(tag, recordType, recordId, insightsPropertyId)
      .then(() => displayToast('Tag has been removed', 'success'))
      .catch(() => displayToast('Failed to remove tag', 'error'));
  };

  const handleOpenModal = useCallback((tag) => {
    setShowModal(true);
    setTagForModal(tag);
  }, []);

  useEffect(() => {
    if (setShowTagsHeader) {
      setShowTagsHeader(!isEmpty(tags));
    }
  }, [tags]);

  return (
    <div className="d-flex gap-2 align-items-center flex-wrap">
      {(tags || []).map(tag => (
        <div className="d-flex align-items-stretch" key={tag}>
          <Badge
            bg=""
            className="bg-connect-tag text-body rounded-end-0 clickable"
            onClick={() => handleOpenModal(tag)}
          >
            <small className="fw-normal">{tag}</small>
          </Badge>
          <Badge
            bg=""
            className="bg-connect-tag text-body rounded-start-0 clickable"
            onClick={() => handleTagDelete(tag)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Badge>
        </div>
      ))}
      <TagPicker buttonProperties={buttonProperties} excludeNames={tags || []} onTagSelected={handleTagSelect} />
      <TagModal showModal={showModal} tagForModal={tagForModal} onHide={() => setShowModal(false)} />
    </div>
  );
}
